import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, ArrowLeft } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface FormData {
  nombre: string;
  tipo: string;
  patente: string;
  marca: string;
  modelo: string;
  anio: string;
  codigo: string;
  clave_qr: string;
  numero_interno: string;
  horometro: {
    lectura: string;
    alertaMantenimiento: string;
  };
}

export const MaquinariaForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<FormData>({
    nombre: '',
    tipo: '',
    patente: '',
    marca: '',
    modelo: '',
    anio: '',
    codigo : '',
    clave_qr: '',
    numero_interno: '',
    horometro: {
      lectura: '0',
      alertaMantenimiento: '250'
    }
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validateCodigo = (value: string) => {
    // Solo permitir letras y números, sin espacios
    return /^[a-zA-Z0-9]+$/.test(value);
  };

  const getCurrentYear = () => {
    return new Date().getFullYear();
  };

  const getYearOptions = () => {
    const currentYear = getCurrentYear();
    const years = [];
    for (let i = 0; i <= 10; i++) {
      years.push(currentYear - i);
    }
    return years;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    if (!validateCodigo(formData.codigo)) {
      setError('El código solo puede contener letras y números, sin espacios');
      setIsSubmitting(false);
      return;
    }

    try {
      const { data: maquinaria, error: maquinariaError } = await supabase
        .from('maquinarias')
        .insert([{
          codigo: formData.codigo,
          nombre: formData.nombre,
          tipo: formData.tipo,
          patente: formData.patente || null,
          marca: formData.marca || null,
          modelo: formData.modelo || null,
          anio: formData.anio ? parseInt(formData.anio) : null,
          numero_interno: formData.numero_interno || null,
          clave_qr: formData.codigo // Use the same code as clave_qr
        }])
        .select()
        .single();

      if (maquinariaError) throw maquinariaError;

      const { error: horometroError } = await supabase
        .from('horometros')
        .insert([{
          maquinaria_id: maquinaria.id,
          lectura: parseInt(formData.horometro.lectura),
          alerta_mantenimiento: parseInt(formData.horometro.alertaMantenimiento)
        }]);

      if (horometroError) throw horometroError;

      navigate('/maquinarias');
    } catch (err) {
      console.error('Error creating machinery:', err);
      setError(err instanceof Error ? err.message : 'Error al crear la maquinaria');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/maquinarias')}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Volver
        </button>
        <h1 className="text-2xl font-bold text-gray-900">Nueva Maquinaria</h1>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded relative">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8 divide-y divide-gray-200">
        <div className="space-y-6">
          <div className="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
            <div className="md:grid md:grid-cols-3 md:gap-6">
              <div className="md:col-span-1">
                <h3 className="text-lg font-medium leading-6 text-gray-900">
                  Información General
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Información básica sobre la maquinaria.
                </p>
              </div>
              <div className="mt-5 md:mt-0 md:col-span-2">
                <div className="grid grid-cols-6 gap-6">
                  <div className="col-span-6">
                    <label htmlFor="codigo" className="block text-sm font-medium text-gray-700">
                      Código
                    </label>
                    <input
                      type="text"
                      name="codigo"
                      id="codigo"
                      required
                      value={formData.codigo}
                      onChange={(e) => setFormData({ ...formData, codigo: e.target.value.toUpperCase() })}
                      className="mt-1 focus:ring-[#004A98] focus:border-[#004A98] block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                      placeholder="Ejemplo: TEST123"
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      Solo letras y números, sin espacios
                    </p>
                  </div>

                  <div className="col-span-6">
                    <label htmlFor="nombre" className="block text-sm font-medium text-gray-700">
                      Nombre
                    </label>
                    <input
                      type="text"
                      name="nombre"
                      id="nombre"
                      required
                      value={formData.nombre}
                      onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                      className="mt-1 focus:ring-[#004A98] focus:border-[#004A98] block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
            <div className="md:grid md:grid-cols-3 md:gap-6">
              <div className="md:col-span-1">
                <h3 className="text-lg font-medium leading-6 text-gray-900">
                  Información del Vehículo
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Detalles específicos del vehículo.
                </p>
              </div>
              <div className="mt-5 md:mt-0 md:col-span-2">
                <div className="grid grid-cols-6 gap-6">
                  <div className="col-span-6 sm:col-span-3">
                    <label htmlFor="tipo" className="block text-sm font-medium text-gray-700">
                      Tipo
                    </label>
                    <select
                      id="tipo"
                      name="tipo"
                      required
                      value={formData.tipo}
                      onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                      className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-[#004A98] focus:border-[#004A98] sm:text-sm"
                    >
                      <option value="">Seleccionar tipo</option>
                      <option value="Excavadora">Excavadora</option>
                      <option value="Camión Tolva">Camión Tolva</option>
                      <option value="Camión Pluma">Camión Pluma</option>
                      <option value="Camión de combustible">Camión de combustible</option>
                      <option value="Camión Aljibe">Camión Aljibe</option>
                      <option value="Mini Cargador">Mini Cargador</option>
                      <option value="Grúa Horquilla">Grúa Horquilla</option>
                      <option value="Cargador Frontal">Cargador Frontal</option>
                    </select>
                  </div>

                  <div className="col-span-6 sm:col-span-3">
                    <label htmlFor="numero_interno" className="block text-sm font-medium text-gray-700">
                      Número Interno
                    </label>
                    <input
                      type="text"
                      name="numero_interno"
                      id="numero_interno"
                      value={formData.numero_interno}
                      onChange={(e) => setFormData({ ...formData, numero_interno: e.target.value })}
                      className="mt-1 focus:ring-[#004A98] focus:border-[#004A98] block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>

                  <div className="col-span-6 sm:col-span-3">
                    <label htmlFor="patente" className="block text-sm font-medium text-gray-700">
                      Patente
                    </label>
                    <input
                      type="text"
                      name="patente"
                      id="patente"
                      value={formData.patente}
                      onChange={(e) => setFormData({ ...formData, patente: e.target.value })}
                      className="mt-1 focus:ring-[#004A98] focus:border-[#004A98] block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>

                  <div className="col-span-6 sm:col-span-3">
                    <label htmlFor="marca" className="block text-sm font-medium text-gray-700">
                      Marca
                    </label>
                    <input
                      type="text"
                      name="marca"
                      id="marca"
                      value={formData.marca}
                      onChange={(e) => setFormData({ ...formData, marca: e.target.value })}
                      className="mt-1 focus:ring-[#004A98] focus:border-[#004A98] block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>

                  <div className="col-span-6 sm:col-span-3">
                    <label htmlFor="modelo" className="block text-sm font-medium text-gray-700">
                      Modelo
                    </label>
                    <input
                      type="text"
                      name="modelo"
                      id="modelo"
                      value={formData.modelo}
                      onChange={(e) => setFormData({ ...formData, modelo: e.target.value })}
                      className="mt-1 focus:ring-[#004A98] focus:border-[#004A98] block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>

                  <div className="col-span-6 sm:col-span-3">
                    <label htmlFor="anio" className="block text-sm font-medium text-gray-700">
                      Año
                    </label>
                    <select
                      id="anio"
                      name="anio"
                      value={formData.anio}
                      onChange={(e) => setFormData({ ...formData, anio: e.target.value })}
                      className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-[#004A98] focus:border-[#004A98] sm:text-sm"
                    >
                      <option value="">Seleccionar año</option>
                      {getYearOptions().map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
            <div className="md:grid md:grid-cols-3 md:gap-6">
              <div className="md:col-span-1">
                <h3 className="text-lg font-medium leading-6 text-gray-900">
                  Horómetro
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Configuración inicial del horómetro.
                </p>
              </div>
              <div className="mt-5 md:mt-0 md:col-span-2">
                <div className="grid grid-cols-6 gap-6">
                  <div className="col-span-6 sm:col-span-3">
                    <label htmlFor="horometro" className="block text-sm font-medium text-gray-700">
                      Lectura Inicial
                    </label>
                    <input
                      type="number"
                      name="horometro"
                      id="horometro"
                      required
                      min="0"
                      value={formData.horometro.lectura}
                      onChange={(e) => setFormData({
                        ...formData,
                        horometro: {
                          ...formData.horometro,
                          lectura: e.target.value
                        }
                      })}
                      className="mt-1 focus:ring-[#004A98] focus:border-[#004A98] block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>

                  <div className="col-span-6 sm:col-span-3">
                    <label htmlFor="alertaMantenimiento" className="block text-sm font-medium text-gray-700">
                      Alerta de Mantenimiento (horas)
                    </label>
                    <input
                      type="number"
                      name="alertaMantenimiento"
                      id="alertaMantenimiento"
                      required
                      min="1"
                      value={formData.horometro.alertaMantenimiento}
                      onChange={(e) => setFormData({
                        ...formData,
                        horometro: {
                          ...formData.horometro,
                          alertaMantenimiento: e.target.value
                        }
                      })}
                      className="mt-1 focus:ring-[#004A98] focus:border-[#004A98] block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                      disabled
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      El mantenimiento se realiza cada 250 horas
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="pt-5">
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => navigate('/maquinarias')}
              className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#004A98]"
              disabled={isSubmitting}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-[#004A98] hover:bg-[#003875] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#004A98]"
            >
              <Save className="h-5 w-5 mr-2" />
              {isSubmitting ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};